import { GetServerSidePropsContext } from 'next'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import LinkButton from '@/src/common/components/marketing/buttons/LinkButton'
import getBuilderPromoBanners from '@/src/common/queries/getBuilderPromoBanners'
import { userCompletedCheckout } from '@/src/common/utilities/gtag'
import { styled, theme } from '@/src/stitches.config'
import { getBuilderBannersOrPromos } from '@/src/common/queries/getBuilderBannersOrPromos'
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useFeatureFlag from '@/src/common/hooks/useFeatureFlag'
import ReferAFriendHero from '@/src/common/components/marketing/heros/ReferAFriendHero'
import {
    REFER_BODY_TEXT,
    REFER_BUTTON_TEXT,
    REFER_GRADIENT_TEXT,
    REFER_HEADER_TEXT,
    REFER_IMAGE_GIRLS,
} from '@/src/common/components/marketing/heros/constants'
import {
    DEFAULT_BUY_PAGE,
    PARTNER_ORDER_STATUS_PAGE,
    REFER_A_FRIEND_PAGE,
} from '@/src/modules/pg-plus/constants'
import HeadlineEyebrow from '@/src/common/components/marketing/headlines/HeadlineEyebrow'
import Image from 'next/image'
import { GradientText } from '@/src/common/components/marketing/heros/StyledComponents'
import { TConfirmationPageProps } from '@/src/modules/checkout/utilities/types'
import getCheckoutConfirmationProps from '@/src/modules/checkout/utilities/getCheckoutConfirmationProps'
import determineBuyPage from '@/src/modules/partners/utilities/determineBuyPage'

const VIEW_ORDER_TEXT = 'View order details'
const CONTINUE_TEXT = 'Continue shopping'

const includeEmailVerification: boolean =
    process.env.NEXT_PUBLIC_ENABLE_BUYER_EMAIL_VERIFICATION === '1'

const PageWrapper = styled('div', {
    width: '$default',
    maxWidth: '$defaultMax',
    margin: 'auto',
    display: 'flex',
    fd: 'column',
    ai: 'center',
    jc: 'center',
    py: 20,
    gap: 30,
    '@media (min-width: 710px)': {
        flexDirection: 'row',
        justifyContent: 'space-between',
        pt: 40,
        pb: 50,
    },
})

const LeftWrapper = styled('div', {
    flex: 1,
    width: '100%',
    '& p': {
        margin: '10px 0',
        lineHeight: '2rem',
        fontSize: '$5',
    },
    '& a': {
        mb: '10px',
        maxWidth: '320px',
    },
    '@media (min-width: 710px)': {
        maxWidth: 430,
    },
})

const StyledH1 = styled('h1', {
    my: 20,
    marginBottom: '20px',
    fontWeight: 'bold',
    fontSize: '2.35rem',
    '@media (min-width: 710px)': {
        fontSize: '2.9rem',
    },
})

const ImageWrapper = styled('div', {
    display: 'flex',
    jc: 'flex-end',
    flex: 0.5,
    '& img': {
        width: '100%',
        maxWidth: 500,
        height: 'auto',
    },
    '@media (min-width: 710px)': {
        '& img': {
            maxWidth: 550,
        },
    },
})

const VerifyEmailText = styled('p', {
    fontWeight: 'bold',
    fontSize: '$6',
    margin: '1rem 0 !important',
})

const StyledLinkButton = styled(LinkButton, {
    mb: 20,
})

const GuestEmailVerificationWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
})

const Confirmation = ({
    email,
    isGuest,
    orderNumber,
    amount,
    discount,
    shipping,
    tax,
    products,
    buyerFirstName,
    buyerLastName,
    partnerData,
}: TConfirmationPageProps) => {
    const [dataLayerEventWasSent, setDataLayerEventWasSent] =
        useState<boolean>(false)

    // if email verification is turned off, default to false
    const showRequiresVerificationMessage: boolean =
        includeEmailVerification && isGuest

    // @todo remove when refer is ready for prod
    const { enabled: referIsEnabled } = useFeatureFlag('refer_enabled')

    const orderStatusPath = partnerData
        ? PARTNER_ORDER_STATUS_PAGE
        : '/order-status'
    const continueShoppingPath: string = determineBuyPage(
        DEFAULT_BUY_PAGE,
        partnerData
    )
    const viewOrderPath: string = `/order-status?order=${orderNumber}&email=${email}`;

    useEffect(() => {
        if (!dataLayerEventWasSent) {
            userCompletedCheckout(
                orderNumber,
                amount,
                discount,
                tax,
                shipping,
                products,
                {
                    buyerFirstName: buyerFirstName,
                    buyerLastName: buyerLastName,
                    email: email,
                }
            )
            setDataLayerEventWasSent(true)
        }
    }, [
        email,
        buyerFirstName,
        buyerLastName,
        amount,
        orderNumber,
        products,
        shipping,
        tax,
        dataLayerEventWasSent,
    ])

    return (
        <>
            <PageWrapper data-testid={'checkout-confirmation'}>
                <LeftWrapper>
                    <HeadlineEyebrow
                        label={'Success'}
                        style={{ fontSize: '1.4rem' }}
                    />
                    <StyledH1 data-testid={'h1Text404'}>
                        Your <GradientText>order</GradientText> has been placed.
                    </StyledH1>
                    <p>
                        Thank you for ordering with BuyGiftCards.com. Your order
                        number is {orderNumber}
                    </p>
                    <p>
                        An email confirmation has been sent to{' '}
                        {email ?? 'your email address'}.
                    </p>
                    {showRequiresVerificationMessage && (
                        <GuestEmailVerificationWrapper>
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                size={'3x'}
                                color={`${theme.colors.error}`}
                                style={{ marginRight: 20 }}
                            />
                            <VerifyEmailText>
                                Your email address must be verified for your
                                order to process. Please be sure to click the
                                &lsquo;Verify my email&rsquo; button or link in
                                your order confirmation email!
                            </VerifyEmailText>
                        </GuestEmailVerificationWrapper>
                    )}
                    <div style={{marginTop: '36px'}}>
                        <StyledLinkButton
                            variant={'primary'}
                            label={'View order details'}
                            url={viewOrderPath}
                        />
                        <StyledLinkButton
                            variant={'primaryOutline'}
                            label={CONTINUE_TEXT}
                            url={continueShoppingPath}
                        />
                    </div>
                </LeftWrapper>
                <ImageWrapper>
                    <Image
                        src={'/images/success_image.png'}
                        height={552}
                        width={695}
                        alt={'order success illustration'}
                        data-testid={'successImage'}
                    />
                </ImageWrapper>
                <Script src="https://apis.google.com/js/platform.js?onload=renderOptIn" />
                <Script id="google-surveyoptin">
                    {`
                    var todaysDate = new Date();
                    var todaysDateFormatted = [
                        todaysDate.getFullYear(),
                        ('0' + (todaysDate.getMonth() + 1)).slice(-2),
                        ('0' + todaysDate.getDate()).slice(-2)
                    ].join('-');
                    
                    window.renderOptIn = function() {
                        window.gapi.load('surveyoptin', function() {
                          window.gapi.surveyoptin.render(
                            {
                              // REQUIRED FIELDS
                              "merchant_id": 5083450289,
                              "order_id": '${orderNumber}',
                              "email": '${email}',
                              "delivery_country": 'US',
                              "estimated_delivery_date": todaysDateFormatted
                            });
                        });
                      }
                `}
                </Script>
            </PageWrapper>
            {referIsEnabled && (
                <ReferAFriendHero
                    headerText={REFER_HEADER_TEXT}
                    gradientText={REFER_GRADIENT_TEXT}
                    bodyText={REFER_BODY_TEXT}
                    buttonLabel={REFER_BUTTON_TEXT}
                    buttonUrl={REFER_A_FRIEND_PAGE}
                    imageUrl={REFER_IMAGE_GIRLS}
                    variant={'image-left'}
                    style={{ marginTop: 30, marginBottom: 30, maxWidth: '90%' }}
                />
            )}
        </>
    )
}

export async function getServerSideProps({
    query,
}: GetServerSidePropsContext<{ o: string }>) {
    const { corporateBanner } = await getBuilderPromoBanners()
    const { consumerData: consumerBanner } = await getBuilderBannersOrPromos(
        true
    )

    const props = await getCheckoutConfirmationProps(query)
    const { orderNumber } = props

    // if no order number present, return not found
    if (!orderNumber) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            consumerBanner,
            corporateBanner,
            ...props,
            content: {
                data: {
                    title: 'Thank You for Choosing BuyGiftCards.com',
                    description:
                        'Thank you for placing your order on BuyGiftCards.com!',
                },
            },
        },
    }
}

export default Confirmation
